import * as React from 'react';
import PropTypes from 'prop-types';
import useSlotProps from '@mui/utils/useSlotProps';
import type { WithDataAttributes } from '@mui/utils/types';
import type { SeriesId } from '@mui/x-charts/internals';
import { useUtilityClasses } from './heatmapClasses';
import { HeatmapCell } from './HeatmapCell';
import type { HeatmapCellOwnerState, HeatmapCellProps } from './HeatmapCell';
import type { CellPropsOverrides } from '../models/chartsSlotsComponentsPropsPro';

export interface HeatmapItemSlots {
  /**
   * The component that renders the heatmap cell.
   * @default HeatmapCell
   */
  cell?: React.ElementType<HeatmapCellProps & CellPropsOverrides>;
}

export interface HeatmapItemSlotProps {
  cell?: WithDataAttributes<Partial<HeatmapCellProps> & CellPropsOverrides>;
}

export interface HeatmapItemProps {
  seriesId: SeriesId;
  value: number;
  /**
   * The width of the element.
   */
  width: number;
  /**
   * The height of the element.
   */
  height: number;
  /**
   * The position of the element in the x-axis.
   */
  x: number;
  /**
   * The position of the element in the y-axis.
   */
  y: number;
  xIndex: number;
  yIndex: number;
  color: string;
  isHighlighted?: boolean;
  isFaded?: boolean;
  /**
   * The border radius of the heatmap cell in pixels.
   */
  borderRadius?: number;
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps?: HeatmapItemSlotProps;
  /**
   * Overridable component slots.
   * @default {}
   */
  slots?: HeatmapItemSlots;
}

/**
 * @ignore - internal component.
 */
function HeatmapItem(props: HeatmapItemProps) {
  const {
    seriesId,
    color,
    value,
    isHighlighted = false,
    isFaded = false,
    borderRadius,
    xIndex,
    yIndex,
    slotProps = {},
    slots = {},
    ...other
  } = props;

  const ownerState: HeatmapCellOwnerState = {
    seriesId,
    color,
    isFaded,
    isHighlighted,
    value,
  };

  const classes = useUtilityClasses(ownerState);

  const Cell = slots?.cell ?? HeatmapCell;
  const cellProps = useSlotProps({
    elementType: Cell,
    additionalProps: {
      rx: borderRadius,
      ry: borderRadius,
      'data-highlighted': isHighlighted || undefined,
      'data-faded': isFaded || undefined,
    },
    externalForwardedProps: { ...other },
    externalSlotProps: slotProps.cell,
    ownerState,
    className: classes.cell,
  });

  // `useSlotProps` returns a loosely-typed object; the required `x/y/width/height` flow in via `...other`.
  return <Cell {...(cellProps as unknown as HeatmapCellProps & CellPropsOverrides)} />;
}

HeatmapItem.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  color: PropTypes.string.isRequired,
  height: PropTypes.number.isRequired,
  seriesId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: PropTypes.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: PropTypes.object,
  value: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
} as any;

export { HeatmapItem };
